import qs from "qs";
import Timeout from "await-timeout";
import POSConfigModule from "@/store/modules/pos-config";
import request from "@/utils/request";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Retry a call while the backend can't yet resolve a NOL order to its legacy
 * booking. The order-based endpoints return HTTP 409 (`NOT_SYNCED`) for ~1 min
 * after completion, while the sync worker creates the legacy booking; retry on
 * 409 with a short delay, then surface the last error.
 */
async function withNotSyncedRetry<T>(
  fn: () => Promise<T>,
  retries = 4,
  delayMs = 3000
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err?.response?.status === 409 && attempt < retries) {
        await Timeout.set(delayMs);
        continue;
      }
      throw err;
    }
  }
}

// ---------------------------------------------------------------------------
// Order tickets
// ---------------------------------------------------------------------------

/**
 * Download a single order ticket PDF by its NOL token.
 */
export async function getDownloadOrderTicket(
  orderId: string,
  token: string
): Promise<string | null> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/ticket/url?token=${token}`,
      method: "get",
    })
  );
  if (!response.data.url) return null;
  return response.data.url;
}

/**
 * Download all of an order's ticket PDFs (omit token = all tickets).
 */
export async function getDownloadOrderTickets(
  orderId: string
): Promise<string | null> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/ticket/url`,
      method: "get",
    })
  );
  if (!response.data.url) return null;
  return response.data.url;
}

/**
 * Generate an order's ticket files.
 */
export async function postGenerateOrderFiles(orderId: string): Promise<number> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/group/${POSConfigModule.groupId}/order/posGenerateFiles`,
      method: "post",
      data: { orderId },
    })
  );
  return response.status;
}

/**
 * Email an order's tickets.
 */
export async function postOrderEmail(data: {
  orderId: string;
  email: string;
  first_name?: string;
  last_name?: string;
}): Promise<number> {
  const { orderId, email, first_name, last_name } = data;
  const params = { email, first_name, last_name };
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/pos/email`,
      method: "post",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      data: qs.stringify(params),
    })
  );
  return response.status;
}

// ---------------------------------------------------------------------------
// Order invoice
// ---------------------------------------------------------------------------

/**
 * Generate an order's invoice.
 */
export async function generateOrderInvoice(orderId: string): Promise<number> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/invoice`,
      method: "post",
    })
  );
  return response.status;
}

/**
 * Download an order's invoice PDF link.
 */
export async function downloadOrderInvoice(
  orderId: string
): Promise<string | null> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/invoice/link`,
      method: "get",
    })
  );
  if (!response.data.data.url) return null;
  return response.data.data.url;
}

/**
 * Send an order's invoice email.
 * @param orderId Order id
 * @param customerOnly if set to 1, email is sent to the customer only
 */
export async function sendOrderInvoiceEmail(
  orderId: string,
  customerOnly?: number
): Promise<number> {
  const param = customerOnly === 1 ? customerOnly : 0;
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/invoice/send-email?email-customer-only=${param}`,
      method: "post",
    })
  );
  return response.status;
}

// ---------------------------------------------------------------------------
// Printing
// ---------------------------------------------------------------------------

/**
 * Print an order's tickets (server-side / PrintNode). Resolves the order to its
 * legacy booking server-side, so it may answer 409 NOT_SYNCED until the sync
 * completes — retried by `withNotSyncedRetry`.
 * @param orderId Order id
 * @param printerId Target printer id
 */
export async function postOrderPrint(data: {
  orderId: string;
  printerId?: number | string;
}): Promise<number> {
  const { orderId, ...params } = data;
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/booking/order/${orderId}/print`,
      method: "post",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      data: qs.stringify(params),
    })
  );
  return response.status;
}
